import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Download, Trash2, Upload, X } from 'lucide-react';
import Navbar from './Navbar';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile } from '@ffmpeg/util';




const sanitizeFileName = (fileName) => {
    return fileName.replace(/[^a-zA-Z0-9._-]/g, '_');
};

const validateVideoFile = async (file) => {
    const validVideoFormats = [
        'video/mp4',
        'video/webm',
        'video/x-matroska',
        'video/quicktime',
        'video/x-msvideo',
        'video/x-ms-wmv',
        'video/mpeg',
        'video/3gpp',
        'video/x-flv',
        'video/ogg'
    ];
    
    if (!file) {
        throw new Error('No file provided');
    }

    if (!validVideoFormats.includes(file.type)) {
        throw new Error('Invalid video format');
    }

    const maxSize = 10 * 1024 * 1024 * 1024; // 10GB
    if (file.size > maxSize) {
        throw new Error('File size exceeds 10GB limit');
    }

    return true;
};

class RateLimiter {
    constructor(maxRequests = 10, timeWindow = 60000) {
        this.maxRequests = maxRequests;
        this.timeWindow = timeWindow;
        this.requests = [];
    }

    canMakeRequest() {
        const now = Date.now();
        this.requests = this.requests.filter(time => now - time < this.timeWindow);
        
        if (this.requests.length >= this.maxRequests) {
            return false;
        }
        
        this.requests.push(now);
        return true;
    }
}

const rateLimiter = new RateLimiter();
const ffmpeg = new FFmpeg();



const PopupMessage = ({ messages, onClose }) => {
    return (
        <div className="popup-container">
            <button className="close-btn" onClick={onClose}>
                <X size={24} />
            </button>
            <div className="popup-message">
                {messages.map((message, index) => (
                    <p key={index} className="message-text">{message}</p>
                ))}

                
            </div>
           
            <style jsx>{`
                .popup-container {
                    position: fixed;
                    top: 50%;
                    left: 50%;
                    transform: translate(-50%, -50%);
                    z-index: 1000;
                }
                .popup-message {
                    background-color: rgba(0, 0, 0, 0.7);
                    color: white;
                    padding: 10px 20px;
                    border-radius: 5px;
                    max-width: 300px;
                    text-align: center;
                    font-size: 14px;
                    max-height: 80vh;
                    overflow-y: auto;
                    word-break: break-word;
                    white-space: normal; 
                }
                .popup-message::-webkit-scrollbar {
                    width: 1.67vh;
                }
                .popup-message::-webkit-scrollbar-track {
                    background-color: rgba(217, 217, 217, 0.3);
                    border-radius: 1.11vh;
                }
                .popup-message::-webkit-scrollbar-thumb {
                    background-color: rgba(255, 255, 255, 0.5);
                    border-radius: 0.74vh;
                    border: 0.37vh solid transparent;
                    background-clip: padding-box;
                }
                .popup-message::-webkit-scrollbar-thumb:hover {
                    background-color: rgba(255, 255, 255, 0.7);
                }
                .close-btn {
                    position: absolute;
                    top: -30px;
                    right: -30px;
                    background: none;
                    border: none;
                    color: white;
                    cursor: pointer;
                    padding: 5px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background-color: rgba(0, 0, 0, 0.7);
                    border-radius: 50%;
                    width: 30px;
                    height: 30px;
                }
            `}</style>
        </div>
    );
};

const FileItem = ({ file, onDownload }) => {
    const [shouldScroll, setShouldScroll] = useState(false);
    const contentRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        const checkIfShouldScroll = () => {
            if (contentRef.current && containerRef.current) {
                const contentWidth = contentRef.current.offsetWidth;
                const containerWidth = containerRef.current.offsetWidth;
                setShouldScroll(contentWidth > containerWidth - 40);
            }
        };

        checkIfShouldScroll();
        window.addEventListener('resize', checkIfShouldScroll);
        return () => window.removeEventListener('resize', checkIfShouldScroll);
    }, [file.name]);

    return (
        <div className="file-item">
            <button className="download-btn" onClick={() => onDownload(file)}>
                <Download size={20} />
            </button>
            <div className="filename-container" ref={containerRef}>
                {!shouldScroll ? (
                    <div className="filename-content" ref={contentRef}>
                        <span className="file-name">{file.name}</span>
                    </div>
                ) : (
                    <>
                        <div ref={contentRef} style={{ position: 'absolute', visibility: 'hidden', whiteSpace: 'nowrap' }}>
                            <span className="file-name">{file.name}</span>
                        </div>
                        <div className="fade-outer fade-left"></div>
                        <div className="fade-inner fade-left"></div>
                        <div className="filename-scroll">
                            <div className="filename-content">
                                <span className="file-name">{file.name}</span>
                                <span className="file-name">{file.name}</span>
                            </div>
                            <div className="filename-content">
                                <span className="file-name">{file.name}</span>
                                <span className="file-name">{file.name}</span>
                            </div>
                        </div>
                        <div className="fade-inner fade-right"></div>
                        <div className="fade-outer fade-right"></div>
                    </>
                )}
            </div>
        </div>
    );
};

const VideoToVideo = () => {
    const [ffmpegLoaded, setFfmpegLoaded] = useState(false);
    const [showTwoSquares, setShowTwoSquares] = useState(true);
    const [files, setFiles] = useState({});
    const [loadingFormats, setLoadingFormats] = useState(new Set());
    const [popupMessages, setPopupMessages] = useState([]);
    const fileListRefs = useRef({});
    const fileInputRefs = useRef({});

    const videoFormats = ['mp4', 'webm', 'avi', 'mov', 'mkv'];

    const updateSquareCount = useCallback(() => {
        const squareSize = window.innerHeight * 0.3333;
        const containerWidth = window.innerWidth;
        setShowTwoSquares(containerWidth >= squareSize * 2 + window.innerWidth * 0.1);
    }, []);

    useEffect(() => {
        const loadFFmpeg = async () => {
    const ffmpegTimeout = setTimeout(() => {
        setPopupMessages(['FFmpeg loading timeout. Please refresh the page.']);
    }, 30000);
    try {
        await ffmpeg.load();
        clearTimeout(ffmpegTimeout);
        setFfmpegLoaded(true);
    } catch (error) {
        console.error('Failed to load FFmpeg:', error);
        clearTimeout(ffmpegTimeout);
        setPopupMessages(['Failed to load Video conversion library']);
    }
};
        loadFFmpeg();
    }, []);

    useEffect(() => {
        updateSquareCount();
        window.addEventListener('resize', updateSquareCount);
        return () => window.removeEventListener('resize', updateSquareCount);
    }, [updateSquareCount]);

    useEffect(() => {
        const checkScrollbars = () => {
            videoFormats.forEach(format => {
                const fileList = fileListRefs.current[format];
                if (fileList) {
                    const hasScrollbar = fileList.scrollHeight > fileList.clientHeight;
                    fileList.style.setProperty('--padding-right', hasScrollbar ? '1.11vh' : '0');
                }
            });
        };

        checkScrollbars();
        window.addEventListener('resize', checkScrollbars);
        return () => window.removeEventListener('resize', checkScrollbars);
    }, [files, videoFormats]);

    const convertVideoFormat = async (file, targetFormat) => {
    if (!ffmpegLoaded) {
        throw new Error('Conversion library not loaded');
    }

    const inputFileName = 'input_video.' + file.name.split('.').pop();
    const outputFileName = `output.${targetFormat}`;

    try {
        // Write actual file data instead of mock
        await ffmpeg.writeFile(inputFileName, await fetchFile(file));
        
        // FFmpeg command for video format conversion
        const ffmpegArgs = ['-i', inputFileName];
        
        // Add format-specific parameters (keep your existing logic)
        switch (targetFormat) {
            case 'mp4':
                ffmpegArgs.push('-c:v', 'libx264', '-c:a', 'aac', '-preset', 'medium', '-crf', '23');
                break;
            case 'webm':
                ffmpegArgs.push('-c:v', 'libvpx-vp9', '-c:a', 'libopus', '-crf', '30', '-b:v', '0');
                break;
            case 'avi':
                ffmpegArgs.push('-c:v', 'libx264', '-c:a', 'mp3', '-preset', 'medium');
                break;
            case 'mov':
                ffmpegArgs.push('-c:v', 'libx264', '-c:a', 'aac', '-preset', 'medium', '-crf', '23');
                break;
            case 'mkv':
                ffmpegArgs.push('-c:v', 'libx264', '-c:a', 'aac', '-preset', 'medium', '-crf', '23');
                break;
        }
        
        ffmpegArgs.push(outputFileName);
        await ffmpeg.exec(ffmpegArgs);

        // Read actual converted data
        const data = await ffmpeg.readFile(outputFileName);
        const convertedFile = new File(
            [data.buffer],
            `${file.name.split('.')[0]}.${targetFormat}`,
            { type: `video/${targetFormat}` }
        );

        // Cleanup
        await Promise.all([
            ffmpeg.deleteFile(inputFileName),
            ffmpeg.deleteFile(outputFileName)
        ]);

        return convertedFile;
    } catch (error) {
        console.error('Conversion error:', error);
        throw error;
    }
};

    const handleFileUpload = async (event, targetFormat) => {
        if (!rateLimiter.canMakeRequest()) {
            setPopupMessages(['Too many requests. Please wait before trying again']);
            return;
        }

        if (!ffmpegLoaded) {
            setPopupMessages(['Video converter is still loading...']);
            return;
        }

        try {
            const uploadedFiles = event.dataTransfer?.files || event.target?.files;
            if (!uploadedFiles?.length) {
                throw new Error('No files selected');
            }

            setLoadingFormats(prev => new Set([...prev, targetFormat]));

            for (const file of uploadedFiles) {
                try {
                    await validateVideoFile(file);
                    const sanitizedName = sanitizeFileName(file.name);
                    const fileWithSanitizedName = new File([file], sanitizedName, { type: file.type });
                    
                    const convertedFile = await convertVideoFormat(fileWithSanitizedName, targetFormat);
                    setFiles(prev => ({
                        ...prev,
                        [targetFormat]: [...(prev[targetFormat] || []), convertedFile]
                    }));
                } catch (error) {
                    console.error('File processing error:', error);
                    setPopupMessages(prev => [...prev, `Failed to process "${file.name}": ${error.message}`]);
                }
            }
        } catch (error) {
            console.error('Upload error:', error);
            setPopupMessages(['An error occurred while processing the video files']);
        } finally {
            setLoadingFormats(prev => {
                const newSet = new Set(prev);
                newSet.delete(targetFormat);
                return newSet;
            });
        }
    };

    const downloadFile = (file) => {
        try {
            if (!file || !(file instanceof File)) {
                throw new Error('Invalid file object');
            }

            const fileExtension = file.name.split('.').pop().toLowerCase();
            if (!videoFormats.includes(fileExtension)) {
                throw new Error('Invalid file type');
            }

            const sanitizedName = sanitizeFileName(file.name);
            const blob = new Blob([file], { type: file.type });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = sanitizedName;
            a.rel = 'noopener noreferrer';
            
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            
            setTimeout(() => {
                URL.revokeObjectURL(url);
            }, 100);
        } catch (error) {
            console.error('Download error:', error);
            setPopupMessages(['Failed to download file']);
        }
    };

    const downloadAllFiles = (formatFiles) => {
        formatFiles.forEach(file => {
            downloadFile(file);
        });
    };

    const handleDragOver = (e) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = (e, format) => {
        e.preventDefault();
        e.stopPropagation();
        handleFileUpload(e, format);
    };

    const clearAllFiles = (format) => {
        setFiles(prevFiles => ({
            ...prevFiles,
            [format]: []
        }));
    };

    const triggerFileInput = (format) => {
        if (fileInputRefs.current[format]) {
            fileInputRefs.current[format].value = '';
            fileInputRefs.current[format].click();
        }
    };

    const renderSquare = (format) => (
        <div
            className="square"
            onDrop={(e) => handleDrop(e, format)}
            onDragOver={handleDragOver}
            onClick={() => triggerFileInput(format)}
        >
            <div className="format format-input">Video to .{format.toLowerCase()}</div>
            
            <input
                type="file"
                multiple
                accept="video/*,.mp4,.webm,.mov,.avi,.mkv,.wmv,.mpeg,.mpg,.3gp,.flv,.ogv"
                onChange={(e) => handleFileUpload(e, format)}
                style={{ display: 'none' }}
                ref={el => fileInputRefs.current[format] = el}
            />
        </div>
    );

    const renderOutputSquare = (format) => (
        <div className={`square output ${(files[format]?.length || loadingFormats.has(format)) ? 'has-files' : ''}`}>
            {(!files[format]?.length && !loadingFormats.has(format)) ? (
                <div className="format">Output</div>
            ) : (
                <div className="file-list-container">
                    <div
                        className="file-list"
                        ref={el => fileListRefs.current[format] = el}
                        onWheel={(e) => e.stopPropagation()}
                    >
                        {loadingFormats.has(format) && <div className="loading">Converting video...</div>}
                        {files[format]?.map((file, idx) => (
                            <FileItem key={idx} file={file} onDownload={downloadFile} />
                        ))}
                    </div>
                    {(files[format]?.length > 0 || loadingFormats.has(format)) && (
                        <div className="button-container">
                            {!showTwoSquares && (
                                <button
                                    className="action-btn upload-btn"
                                    onClick={() => triggerFileInput(format)}
                                >
                                    {format.toLowerCase()}
                                </button>
                            )}
                            {files[format]?.length > 0 && (
                                <>
                                    <button
                                        className="action-btn download-all-btn"
                                        onClick={() => downloadAllFiles(files[format])}
                                    >
                                        <Download size={20} /> Download All
                                    </button>
                                    <button
                                        className="action-btn clear-all-btn"
                                        onClick={() => clearAllFiles(format)}
                                    >
                                        <Trash2 size={20} />
                                    </button>
                                </>
                            )}
                        </div>
                    )}
                </div>
            )}
            
            <input
                type="file"
                multiple
                accept="video/*,.mp4,.webm,.mov,.avi,.mkv,.wmv,.mpeg,.mpg,.3gp,.flv,.ogv"
                onChange={(e) => handleFileUpload(e, format)}
                style={{ display: 'none' }}
                ref={el => fileInputRefs.current[format] = el}
            />
        </div>
    );

    return (
        <div className="container">
            <Navbar />
            {popupMessages.length > 0 && (
                <PopupMessage
                    messages={popupMessages}
                    onClose={() => setPopupMessages([])}
                />
            )}
            {videoFormats.map((format, index) => (
                <div className="squares" key={index} data-format={format}>
                    {showTwoSquares ? (
                        <>
                            {renderSquare(format)}
                            {renderOutputSquare(format)}
                        </>
                    ) : (
                        loadingFormats.has(format) || files[format]?.length > 0
                            ? renderOutputSquare(format)
                            : renderSquare(format)
                    )}
                </div>
            ))}
            
 <div class="features-section">
        <div class="hero-section">
            <h1>Free Video Converter Online - Convert Video Files Instantly</h1>
            <p class="hero-description">Convert any video file between MP4, WebM, AVI, MOV, and MKV formats with our fast, secure, and free online converter. No registration required.</p>
        </div>
        <h2>Why Choose Our Video Converter</h2>
        <div class="features-grid">
            <div class="feature">
                <h3>High-Quality Video Conversion</h3>
                <p>Maintain excellent video quality with our professional-grade conversion engine. MP4 files use H.264 codec with optimized settings, and WebM files utilize VP9 codec for superior compression.</p>
            </div>
            <div class="feature">
                <h3>100% Free & Secure</h3>
                <p>No hidden fees, subscriptions, or watermarks. Your videos are processed locally in your browser and never uploaded to any server, ensuring complete privacy.</p>
            </div>
            <div class="feature">
                <h3>Batch Video Processing</h3>
                <p>Save time by converting multiple video files simultaneously with our efficient batch processing capability. Perfect for content creators and professionals.</p>
            </div>
            <div class="feature">
                <h3>Multiple Format Support</h3>
                <p>Convert between popular video formats including MP4, WebM, AVI, MOV, and MKV to meet all your video compatibility needs across different devices and platforms.</p>
            </div>
        </div>
    </div>

    <div class="how-it-works">
        <h2>How to Convert Video Files</h2>
        <div class="steps">
            <div class="step">
                <div class="step-number">1</div>
                <p>Select your target video format from the available options (MP4, WebM, AVI, MOV, MKV)</p>
            </div>
            <div class="step">
                <div class="step-number">2</div>
                <p>Upload your video files by clicking on the format square or drag and drop directly from your computer</p>
            </div>
            <div class="step">
                <div class="step-number">3</div>
                <p>Wait for the conversion to complete - processing time varies based on file size and format</p>
            </div>
            <div class="step">
                <div class="step-number">4</div>
                <p>Download your converted video files individually or all at once with our bulk download feature</p>
            </div>
        </div>
    </div>

    <div class="faq-section">
        <h2>Frequently Asked Questions</h2>
        <div class="faq-item">
            <h3>Is this video converter completely free?</h3>
            <p>Yes, our video converter is 100% free to use with support for files up to 10GB in size. We don't require registration, payment information, or impose conversion limits.</p>
        </div>
        <div class="faq-item">
            <h3>Which video formats does this converter support?</h3>
            <p>Our converter supports conversion between MP4, WebM, AVI, MOV, and MKV formats. You can convert from any supported format to any other format with maintained quality.</p>
        </div>
        <div class="faq-item">
            <h3>Is my video quality preserved during conversion?</h3>
            <p>We use high-quality conversion settings to maintain video fidelity. MP4 files use H.264 codec with CRF 23, WebM uses VP9 with optimized bitrates, and other formats use best-practice encoding parameters.</p>
        </div>
        <div class="faq-item">
            <h3>Are my video files safe and private?</h3>
            <p>Absolutely. All processing happens locally in your browser using WebAssembly technology. Your video files are never uploaded to any server, ensuring complete privacy and security.</p>
        </div>
        <div class="faq-item">
            <h3>What's the maximum file size I can convert?</h3>
            <p>Our converter supports video files up to 10GB in size. Large files may take longer to process depending on your device's capabilities and the complexity of the conversion.</p>
        </div>
        <div class="faq-item">
            <h3>Can I convert multiple video files at once?</h3>
            <p>Yes, our tool supports batch conversion. You can upload multiple video files simultaneously and convert them all efficiently with our parallel processing system.</p>
        </div>
        <div class="faq-item">
            <h3>Do you support 4K and HD video conversion?</h3>
            <p>Yes, our converter handles videos of all resolutions including 4K, Full HD, and standard definition. The original resolution and aspect ratio are preserved during conversion.</p>
        </div>
        <div class="faq-item">
            <h3>How long does video conversion take?</h3>
            <p>Conversion time depends on file size, format complexity, and your device's processing power. Typical conversions range from 30 seconds to several minutes for large files.</p>
        </div>
    </div>

    <div class="use-cases">
        <h2>Popular Video Conversion Use Cases</h2>
        <div class="use-cases-grid">
            <div class="use-case">
                <h3>Social Media Content</h3>
                <p>Convert videos to MP4 format for optimal compatibility with Instagram, Facebook, TikTok, and YouTube. Ensure your content plays smoothly across all platforms.</p>
            </div>
            <div class="use-case">
                <h3>Web Video Optimization</h3>
                <p>Convert to WebM format for faster web loading times and better compression. Perfect for websites, blogs, and online courses requiring efficient video delivery.</p>
            </div>
            <div class="use-case">
                <h3>Device Compatibility</h3>
                <p>Convert videos to formats compatible with your specific devices - from smartphones and tablets to smart TVs and gaming consoles.</p>
            </div>
            <div class="use-case">
                <h3>Video Editing Preparation</h3>
                <p>Convert raw footage to editing-friendly formats like MOV or AVI for smoother workflow in professional video editing software like Adobe Premiere or Final Cut Pro.</p>
            </div>
            <div class="use-case">
                <h3>File Size Reduction</h3>
                <p>Compress large video files by converting to more efficient formats, saving storage space while maintaining acceptable quality for sharing and archiving.</p>
            </div>
            <div class="use-case">
                <h3>Legacy Format Support</h3>
                <p>Convert modern video formats to older formats like AVI for compatibility with legacy systems, older devices, or specific software requirements.</p>
            </div>
        </div>
    </div>

    <div class="about-section">
        <h2>About Our Video Converter</h2>
        <p>Our free online video converter was built with a focus on simplicity, security, and professional-quality results. We use the powerful FFmpeg technology running directly in your browser through WebAssembly to perform conversions locally without uploading your files to any server.</p>
        <p>Whether you're a content creator, social media manager, video editor, or casual user looking to change video formats for better compatibility, our tool provides professional-grade conversions without the need for complex software installations or paid subscriptions.</p>
        <p>The converter supports all major video formats and maintains the highest quality standards while offering fast processing speeds. Our commitment to privacy means your videos never leave your device, making it the safest option for sensitive or personal content conversion.</p>
    </div>

            <style jsx>{`
                .container {
                    display: flex;
                    flex-direction: column;
                    justify-content: flex-start;
                    align-items: center;
                    margin-top:33.33vh;
                     
                    box-sizing: border-box;
                    width: 100%;
                    min-height: 100vh;
                    
                }
                .squares {
                    display: flex;
                    gap: 6.09vw;
                    width: 100%;
                    justify-content: center;
                    margin-bottom: 33.33vh;
                }
                .square {
                    width: 44.44vh;
                    height: 33.33vh;
                    background-color: #007AFF;
                    position: relative;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    cursor: pointer;
                    border-radius: 1.11vh;
                    box-sizing: border-box;
                }
                .square.output {
                    width: 44.44vh;
                    overflow: hidden;
                    background: #D9D9D9;
                    border: none;
                    padding: 0;
                }
                .format {
                    color: white;
                    font-size: 5.28vh;
                    font-weight: bold;
                    pointer-events: none;
                    font-family: "DM Sans", sans-serif;
                    font-optical-sizing: auto;
                    font-weight: 400;
                    font-style: normal;
                }
                .format-input {
                    font-size: 4.17vh;
                }
                .output.has-files {
                    background: transparent;
                }
                .file-list-container {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    width: 100%;
                    font-family: "Lexend", sans-serif;
                    font-optical-sizing: auto;
                    font-weight: 400;
                    font-style: normal;
                }
                .file-list {
                    flex: 1;
                    overflow-y: auto;
                    display: flex;
                    flex-direction: column;
                    gap: 1.11vh;
                    padding-right: var(--padding-right, 0);
                    overscroll-behavior: contain; 
                }
                .file-list::-webkit-scrollbar {
                    width: 1.67vh;
                }
                .file-list::-webkit-scrollbar-track {
                    background-color: #D9D9D9;
                    border-radius: 1.11vh;
                }
                .file-list::-webkit-scrollbar-thumb {
                    background-color: rgba(27, 31, 38, 0.72);
                    border-radius: 0.74vh;
                    border: 0.37vh solid transparent;
                    background-clip: padding-box;
                }
                .file-list::-webkit-scrollbar-thumb:vertical {
                    min-height: 24px;
                }
                .file-list::-webkit-scrollbar-button {
                    height: 0vh;
                    background-color: #000;
                }
                .file-item {
                    display: flex;
                    align-items: center;
                    height: 4.63vh;
                    gap: 1.11vh;
                    padding: 0 0;
                }
                .filename-container {
                    flex: 1;
                    overflow: hidden;
                    height: 100%;
                    display: flex;
                    align-items: center;
                    background: #EBEDF0;
                    border-radius: 1.11vh;
                    position: relative;
                }
                .fade-outer, .fade-inner {
                    position: absolute;
                    top: 0;
                    bottom: 0;
                    z-index: 2;
                }
                .fade-outer.fade-left {
                    width: 1.11vh;
                    left: 1.11vh;
                    background: linear-gradient(to right, #EBEDF0, rgba(235,237,240,0));
                }
                .fade-outer.fade-right {
                    width: 1.11vh;
                    right: 1.11vh;
                    background: linear-gradient(to left, #EBEDF0, rgba(235,237,240,0));
                }
                .fade-inner.fade-left {
                    width: 1.11vh;
                    left: 0px;
                    background: #EBEDF0;
                }
                .fade-inner.fade-right {
                    width: 1.11vh;
                    right: 0px;
                    background: #EBEDF0;
                }
                .filename-scroll {
                    display: flex;
                    white-space: nowrap;
                    animation: scroll-left 20s linear infinite;
                }
                .filename-content {
                    display: flex;
                    padding: 0 1.11vh;
                }
                .file-name {
                    color: #000;
                    font-size: 1.67vh;
                    font-weight: 500;
                    padding-right: 2.22vh;
                }
                .download-btn {
                    background-color: #007AFF;
                    color: white;
                    border: none;
                    width: 4.63vh;
                    height: 4.63vh;
                    border-radius: 1.11vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    cursor: pointer;
                    transition: background-color 0.3s ease;
                    padding: 0;
                }
                .download-btn svg {
                    width: 2.22vh; 
                    height: 2.22vh;               
                }
                .download-btn:hover {
                    background-color: #0056b3;
                }
                .button-container {
                    display: flex;
                    justify-content: space-between;
                    padding-top: 1.11vh;
                }
                .action-btn {
                    color: white;
                    border: none;
                    height: 4.63vh;
                    border-radius: 4px;
                    font-size: 14px;
                    font-weight: 500;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 1.11vh;
                    cursor: pointer;
                    transition: background-color 0.3s ease;
                    padding: 0 1.11vh;
                }
                .upload-btn {
                    background-color: #FF9500;
                    flex-grow: 1;
                    margin-right: 1.11vh;
                    width:13.33vh;
                    border-radius:1.11vh;
                    font-family: "DM Sans", sans-serif;
                    font-optical-sizing: auto;
                    font-weight: 400;
                    font-style: normal;
                }
                .upload-btn:hover {
                    background-color: #138496;
                }
                .download-all-btn {
                    background-color: #34C759;
                    flex-grow: 2;
                    font-size: 1.48vh;
                    border-radius: 1.11vh;
                    font-family: "Lexend", sans-serif;
                    font-optical-sizing: auto;
                    font-weight: 400;
                    font-style: normal;
                }
                .download-all-btn:hover {
                    background-color: #218838;
                }
                .clear-all-btn {
                    background-color: #FF3B30;
                    width: 4.63vh;
                    height: 4.63vh;
                    margin-left: 1.11vh;
                    border-radius: 1.11vh;
                }
                .clear-all-btn:hover {
                    background-color: #c82333;
                }
                .action-btn svg {
                    width: 2.22vh;
                    height: 2.22vh;
                }
                .loading {
                    height: 4.63vh;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #333;
                    font-size: 14px;
                    background: #ffffff;
                    padding: 0 1.11vh;
                }
                @keyframes scroll-left {
                    0% {
                        transform: translateX(0);
                    }
                    100% {
                        transform: translateX(-50%);
                    }
                }

.hero-section {
  text-align: center;
  max-width: 800px;
  margin: 0vh auto 18vh auto;
  padding: 0px;
}

.hero-section h1 {
  font-size: 3.5vh;
  color: #333;
  margin-bottom: 2vh;
  font-family: "DM Sans", sans-serif;
}

.hero-description {
  font-size: 2vh;
  color: #666;
  line-height: 1.5;
  font-family: "Lexend", sans-serif;
}

.features-section, .how-it-works, .faq-section, .use-cases, .about-section {
  max-width: 1200px;
  margin: 10vh auto;
  padding: 0 20px;
}

.features-section{
  max-width: 1200px;
  margin: -5vh auto 10vh auto;
  padding: 0 20px;
}


h2 {
  font-size: 2.8vh;
  color: #333;
  text-align: center;
  margin-bottom: 5vh;
  font-family: "DM Sans", sans-serif;
}

.features-grid, .use-cases-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 30px;
}

.feature, .use-case {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

.feature h3, .use-case h3, .faq-item h3 {
  font-size: 2vh;
  color: #007AFF;
  margin-bottom: 10px;
  font-family: "DM Sans", sans-serif;
}

.feature p, .use-case p, .faq-item p, .about-section p {
  font-size: 1.6vh;
  color: #555;
  line-height: 1.6;
  font-family: "Lexend", sans-serif;
}

.steps {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 20px;
}

.step {
  flex: 1 1 200px;
  display: flex;
  align-items: center;
  background: #f8f9fa;
  border-radius: 10px;
  padding: 15px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

.step-number {
  background: #007AFF;
  color: white;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 15px;
  flex-shrink: 0;
}

.step p {
  margin: 0;
  font-size: 1.6vh;
  color: #555;
  font-family: "Lexend", sans-serif;
}

.faq-item {
  background: #f8f9fa;
  border-radius: 10px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
}

@media (max-width: 768px) {
  .features-grid, .use-cases-grid {
    grid-template-columns: 1fr;
  }
  
  .steps {
    flex-direction: column;
  }
}

            `}</style>
        </div>
    );
};

export default VideoToVideo;